import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IStoreBannersService } from "@shared/interfaces/store-banners-service.interface";
import { StoreBannerDto } from "@shared/types/store-banner/store-banner.dto";
import { StoreBannerStatus } from "@shared/types/store-banner/enums/store-banner-status.enum";
import { Button, Form, Input, Modal, Select, Space } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import { useMutation } from "react-query";
import { CampaignBannerDto } from "@shared/types/campaigns/campaign-banner.dto";

interface ChangeStoreBannerModalProps {
  open: boolean;
  banner: StoreBannerDto | null;
  onClose: () => void;
  onSuccess: () => void;
  campaigns: CampaignBannerDto[];
}

const ChangeStoreBannerModal = ({
  open,
  banner,
  onClose,
  onSuccess,
  campaigns,
}: ChangeStoreBannerModalProps) => {
  const [form] = Form.useForm();
  const storeBannersService = appInject<IStoreBannersService>(
    DI_TOKENS.storeBannersService,
  );

  useEffect(() => {
    if (banner) {
      form.setFieldsValue(banner);
    } else {
      form.resetFields();
      form.setFieldValue("status", StoreBannerStatus.ACTIVE);
      form.setFieldValue("campaignId", "-1");
    }
  }, [banner, form]);

  const mutation = useMutation(
    (values: any) => {
      if (banner?.id) {
        return storeBannersService.update({ ...values, id: banner.id });
      }
      return storeBannersService.create(values);
    },
    {
      onSuccess: () => {
        onSuccess();
      },
    },
  );

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      mutation.mutate(values);
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  return (
    <Modal
      open={open}
      title={banner ? "Edit Store Banner" : "Create Store Banner"}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={mutation.isLoading}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: "Please enter the title" }]}
        >
          <Input placeholder="Enter banner title" />
        </Form.Item>

        <Form.Item
          name="titleHighlight"
          label="Title Highlights"
          rules={[{ required: true, message: "Please enter title highlights" }]}
        >
          <Select
            mode="tags"
            placeholder="Enter words to be highlighted in title"
            tokenSeparators={[","]}
          />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <Input.TextArea placeholder="Enter banner description" rows={3} />
        </Form.Item>

        <Form.Item
          name="descriptionHighlight"
          label="Description Highlights"
          rules={[
            { required: true, message: "Please enter description highlights" },
          ]}
        >
          <Select
            mode="tags"
            placeholder="Enter words to be highlighted in description"
            tokenSeparators={[","]}
          />
        </Form.Item>

        <Form.Item
          name="badgeText"
          label="Badge Text"
          rules={[{ required: false }]}
        >
          <Input placeholder="Enter badge text" />
        </Form.Item>

        <Form.Item
          name="buttonUrl"
          label="Button URL"
          rules={[{ required: false }]}
        >
          <Input placeholder="Enter button URL" />
        </Form.Item>

        <Form.Item
          name="buttonTitle"
          label="Button Title"
          rules={[{ required: true, message: "Please enter button title" }]}
        >
          <Input placeholder="Enter button title" />
        </Form.Item>

        <Form.Item
          name="status"
          label="Status"
          rules={[{ required: true, message: "Please select status" }]}
        >
          <Select>
            <Select.Option value={StoreBannerStatus.ACTIVE}>
              Active
            </Select.Option>
            <Select.Option value={StoreBannerStatus.INACTIVE}>
              Inactive
            </Select.Option>
          </Select>
        </Form.Item>

        <div style={{ marginBottom: 8 }}>Allowed Regions</div>
        <Form.List name="allowedCountries">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Space
                  key={key}
                  style={{ display: "flex", marginBottom: 8 }}
                  align="baseline"
                >
                  <Form.Item
                    {...restField}
                    name={[name, "country"]}
                    rules={[
                      {
                        required: true,
                        message: "Missing country",
                      },
                    ]}
                  >
                    <Input placeholder="Country (e.g. IN)" />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, "states"]}
                    rules={[
                      {
                        required: true,
                        message: "Missing states",
                      },
                    ]}
                  >
                    <Select
                      mode="tags"
                      placeholder="States (e.g. KA, MH)"
                      style={{ width: 200 }}
                      tokenSeparators={[",", " "]}
                    />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} />
                </Space>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  Add Allowed Region
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Form.Item
          name="campaignId"
          label="Campaign"
          rules={[
            { required: true, message: "Please select campaign" },
            {
              validator: (_, value) =>
                value === "-1"
                  ? Promise.reject(new Error("Please select a valid campaign"))
                  : Promise.resolve(),
            },
          ]}
          initialValue="-1"
        >
          <Select>
            <Select.Option value="-1">Select Campaign</Select.Option>
            {campaigns.map((campaign) => (
              <Select.Option key={campaign.id} value={campaign.id}>
                {campaign.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChangeStoreBannerModal;
